import { AllValuesWithSize } from '@pubstack/common/src/analytics/query'
import { EMPTY_REFRESH_GROUP, GroupOverlap, RefreshGroup } from '@pubstack/common/src/refresh'
import { useAtomValue } from 'jotai'
import { FunctionComponent, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAnalyticsQueryTanstack } from '~/api/analytics/analytics-query.api'
import { useRefreshGroups } from '~/api/api.hook'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { contextState } from '~/state'
import { PureSettingsRefreshGroupAccordionPage } from './PureSettingsRefreshGroupAccordionPage'

export const SettingsRefreshGroupAccordionPage: FunctionComponent = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const context = useAtomValue(contextState)
  const user = useAuthenticatedUser()
  const isPubstack = user ? user.isAdmin : false

  const analyticsQuery = useAnalyticsQueryTanstack<AllValuesWithSize>('all.values.with.size', [], context, {})
  const { byId: refreshGroupsById, check } = useRefreshGroups(null)

  const devices = [
    { name: 'Desktop', id: 'desktop' },
    { name: 'Mobile', id: 'mobile' },
    { name: 'Tablet', id: 'tablet' },
    { name: 'Others', id: 'other' },
  ]

  const [refreshGroup, setRefreshGroup] = useState<RefreshGroup>()

  const loadRefreshGroup = async () => {
    if (id) {
      const refreshGroup = await refreshGroupsById.get(id)
      setRefreshGroup(refreshGroup)
    }
  }

  const save = async (refreshGroup: RefreshGroup) => {
    if (refreshGroup.id) {
      await refreshGroupsById.put(refreshGroup)
    } else {
      await refreshGroupsById.post(refreshGroup)
    }
    navigate('/settings/refresh-groups')
  }

  const onSave = async (refreshGroup: RefreshGroup, options?: { activateGroup?: boolean; bypassCheck?: boolean }): Promise<void | GroupOverlap> => {
    if (options?.bypassCheck) {
      return save(refreshGroup)
    }
    const overlaps = await check(refreshGroup)
    if (overlaps) {
      return overlaps.conflicts
    } else {
      save({ ...refreshGroup, enabled: options?.activateGroup === undefined ? refreshGroup.enabled : options.activateGroup })
    }
  }

  useEffect(() => {
    setTimeout(() => {
      loadRefreshGroup()
    })
  }, [context, id])

  return (
    <PureSettingsRefreshGroupAccordionPage
      isLoading={refreshGroupsById.loading || analyticsQuery.isFetching}
      isEditing={!!id}
      refreshGroup={refreshGroup ?? EMPTY_REFRESH_GROUP}
      setRefreshGroup={setRefreshGroup}
      adUnits={analyticsQuery.data?.adunits ?? []}
      bidders={analyticsQuery.data?.bidders ?? []}
      devices={devices}
      sites={analyticsQuery.data?.sites ?? []}
      isPubstackRole={isPubstack}
      onSave={onSave}
    />
  )
}
